import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Skeleton,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { showAlert } from "../../../../Services/Shared.service";
import {
  getPackageDriverCreditByPackageId,
  getPackageDriverCreditByCorporateClientId,
  activatePackageDriverCreditMapping,
  deactivatePackageDriverCreditMapping,
  deletePackageDriverCreditMapping,
} from "../../../../Services/DriverCredit.service";
import { GREEN_BUTTON_STYLE } from "../../../../styles/CommonStyles";
import CreateEditDriverCreditForm from "../Forms/CreateEditDriverCreditForm";
import DeleteConfirmDialog from "../../../Common/Component/DeleteConfirmDialog";

const CREDIT_TYPE_MAP = {
  A: "Amount",
  P: "Percentage",
};

function DriverCreditsTable({ packId, corporateClientId }) {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [modalState, setModalState] = useState({ open: false, data: null });
  const [deleteConfirmState, setDeleteConfirmState] = useState({ open: false, row: null, loading: false });

  useEffect(() => {
    if (packId || corporateClientId) fetchTableData();
  }, [packId, corporateClientId]);

  const fetchTableData = useCallback(() => {
    setLoading(true);
    const fetchFn = corporateClientId
      ? getPackageDriverCreditByCorporateClientId(corporateClientId)
      : getPackageDriverCreditByPackageId(packId);
    fetchFn
      .then((resp) => {
        setTableData(resp?.data?.list || resp?.data || []);
      })
      .catch((err) => {
        console.error("Error fetching driver credits:", err);
        showAlert(
          "error",
          err.response?.data?.message || "Failed to fetch driver credits"
        );
      })
      .finally(() => setLoading(false));
  }, [packId, corporateClientId]);

  const handleCreate = useCallback(() => {
    setModalState({ open: true, data: null });
  }, []);

  const handleEdit = useCallback((row) => {
    setModalState({ open: true, data: row });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState({ open: false, data: null });
  }, []);

  const handleSuccess = useCallback(() => {
    handleCloseModal();
    fetchTableData();
  }, [fetchTableData, handleCloseModal]);

  // ── Delete flow ──
  const handleOpenDelete = useCallback((row) => {
    setDeleteConfirmState({ open: true, row, loading: false });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const { row } = deleteConfirmState;
    setDeleteConfirmState((prev) => ({ ...prev, loading: true }));
    try {
      await deletePackageDriverCreditMapping(row.id);
      setDeleteConfirmState({ open: false, row: null, loading: false });
      setTableData((prevData) => prevData.filter((item) => item.id !== row.id));
    } catch (err) {
      console.error("Failed to delete credit mapping", err);
      setDeleteConfirmState((prev) => ({ ...prev, loading: false }));
    }
  }, [deleteConfirmState]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmState({ open: false, row: null, loading: false });
  }, []);

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={50}
            sx={{ mb: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: "1.39vw", fontWeight: "600" }}>
          Driver Credits
        </Typography>
        {/* Placeholder for Create / Edit actions */}
        <Button variant="outlined" sx={GREEN_BUTTON_STYLE} onClick={handleCreate}>
          CREATE CREDIT
        </Button>
      </Box>

      {tableData.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "6px",
          }}
        >
          <Typography sx={{ color: "black" }}>
            No Driver Credits Available
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "22vw",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: "#626262",
                    fontWeight: "bold",
                    fontSize: "1.11vw",
                    height: "3.89vw",
                    maxHeight: "5vw",
                    paddingX: 0,
                    paddingY: "12px",
                    backgroundColor: "#E1F4E4",
                  },
                }}
              >
                <TableCell align="center">Driver Credit</TableCell>
                <TableCell align="center">Credit Type</TableCell>
                <TableCell align="center">Credit Source</TableCell>
                <TableCell align="center">Credit Value</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tableData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "1.11vw",
                      color: "#39B54A",
                      fontWeight: "600",
                      padding: "0.83vw",
                    }}
                  >
                    {row.driverCredit?.category || "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
                  >
                    {CREDIT_TYPE_MAP[row.creditType] || row.creditType || "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
                  >
                    {row.creditSource || "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
                  >
                    {row.creditType === "P"
                      ? `${row.creditValue}%`
                      : row.creditValue ?? "-"}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: "0.83vw" }}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      {row.isActive ? (
                        <Box
                          sx={{
                            display: "flex",
                            backgroundColor: "#E3F5E1",
                            width: "6.11vw",
                            height: "2.01vw",
                            justifyContent: "space-evenly",
                            alignItems: "center",
                            borderRadius: "6px",
                            padding: "6px",
                            border: "1px solid rgba(116, 206, 106, 1)",
                          }}
                        >
                          <Box
                            sx={{
                              width: "0.56vw",
                              height: "0.56vw",
                              background: "rgba(116, 206, 106, 1)",
                              borderRadius: "50%",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.97vw" }}>Yes</Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-around",
                            width: "6.11vw",
                            height: "2.01vw",
                            borderRadius: "6px",
                            padding: "6px",
                            backgroundColor: "#ffe5e5",
                            border: "1px solid #cc0000",
                          }}
                        >
                          <Box
                            sx={{
                              width: "0.56vw",
                              height: "0.56vw",
                              backgroundColor: "#cc0000",
                              borderRadius: "50%",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.97vw", color: "#cc0000" }}>
                            No
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <IconButton onClick={() => handleEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleOpenDelete(row)} sx={{ color: '#D32F2F' }}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateEditDriverCreditForm
        open={modalState.open}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        editData={modalState.data}
        packId={packId}
        corporateClientId={corporateClientId}
      />

      {/* ── Delete Credit Mapping Confirmation ── */}
      <DeleteConfirmDialog
        open={deleteConfirmState.open}
        title="Remove Driver Credit"
        message={
          <>
            You are about to permanently remove the credit associated with this client. This action cannot be undone and will directly affect the driver’s financial standing.{" "}
            <strong>Are you sure you want to proceed?</strong>
          </>
        }
        loading={deleteConfirmState.loading}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
}

export default DriverCreditsTable;
